import React, {useEffect, useMemo, useRef, useState} from 'react';
import {loadColourOptions} from './api';

/**
 * Zeigt den voraussichtlichen Aufschlag bereits an jeder konkreten Option.
 * Entscheidend ist der vollständige Farbschlüssel, nicht nur die Farbart oder
 * der Hersteller. So bleibt beispielsweise BMW atacama unabhängig von einer
 * zusätzlich gewählten Lamborghini-Farbe.
 */
function feeText(option, existingKeys) {
  return option?.is_special ? (existingKeys.includes(option.key) ? '+0 CHF' : '+25 CHF') : '';
}

/** Normalisiert Hexwerte aus Standard-, RAL- und Automobilfarbdaten. */
function swatch(value) {
  const colour = String(value || '').trim();
  if (/^#[0-9a-f]{3,6}$/i.test(colour)) return colour;
  if (/^[0-9a-f]{3,6}$/i.test(colour)) return `#${colour}`;
  return '#f2eee9';
}

/**
 * Eigenes Dropdown für lange Farbtabellen.
 *
 * Native select-Optionen können keine zuverlässigen Farbvorschauen darstellen.
 * Dieses Listbox-ähnliche Steuerelement zeigt daher bei Auto- und RAL-Farben
 * eine Vorschau sowie den konkreten CHF-Aufschlag. Bei Möbelfarben wird die
 * Vorschau bewusst ausgeblendet, weil dafür keine belastbaren Farbwerte vorliegen.
 */
function OptionDropdown({label, options, value, onChange, existingKeys, showSwatch, placeholder}) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const selected = options.find((option) => option.key === value?.key);
  useEffect(() => {
    const close = (event) => { if (root.current && !root.current.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  return (
    <div className="pf-dropdown" ref={root}>
      <span className="pf-field-label">{label}</span>
      <button type="button" className="pf-dropdown__toggle" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        <span>{selected?.label || selected?.colour_name || placeholder}</span>
        {selected && <strong>{feeText(selected, existingKeys)}</strong>}
      </button>
      {open && (
        <div className="pf-dropdown__menu" role="listbox">
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={selected?.key === option.key}
              className={selected?.key === option.key ? 'is-active' : ''}
              key={option.key}
              onClick={() => { onChange(option); setOpen(false); }}
            >
              {showSwatch && <span className="pf-dropdown__swatch" style={{backgroundColor: swatch(option.hex || option.jscolor)}} />}
              <span>{option.label || option.colour_name}</span>
              <strong>{feeText(option, existingKeys)}</strong>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Farbauswahl für die in Kotori_FarbproduktOptionen definierten Zielprodukte.
 *
 * Die Komponente besitzt keine eigene Farbliste. Alle Optionen kommen aus dem
 * lokalen, durch Kotori_Farbverwaltung gepflegten Katalog. Dadurch entsprechen
 * PDP, Anleitungsprodukte und Produktfinder immer derselben Datenquelle.
 */
export default function ColourChooser({sku, allowedColourTypes, value, onChange, existingKeys, t}) {
  const [type, setType] = useState('');
  const [options, setOptions] = useState([]);
  const [manufacturer, setManufacturer] = useState('');
  const [ralGroup, setRalGroup] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Magento liefert das verbindliche Produktprofil. Der Fallback erhält die
  // bisherige Darstellung für ältere Productfinder-API-Antworten.
  const visibleTypes = Array.isArray(allowedColourTypes) && allowedColourTypes.length
    ? allowedColourTypes
    : ['standard', 'auto', 'furniture', 'ral', 'sample'];

  useEffect(() => {
    // Ein Variantenwechsel kann auf eine andere SKU und damit andere
    // Konfigurierbarkeit führen. Eine alte Farbauswahl darf nicht mitwandern.
    setType(''); setOptions([]); setManufacturer(''); setRalGroup(''); setError(''); onChange(null);
  }, [sku]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseType = (nextType) => {
    // Beim Wechsel der Farbart werden alle abhängigen Ebenen geleert. Die
    // Musteroption besitzt nur einen Eintrag und kann deshalb direkt gewählt werden.
    setType(nextType); setManufacturer(''); setRalGroup(''); setOptions([]); setError(''); onChange(null);
    setLoading(true);
    loadColourOptions(sku, nextType)
      .then((items) => {
        setOptions(items);
        if (nextType === 'sample' && items[0]) onChange(items[0]);
      })
      .catch(() => setError(t('colourLoadError')))
      .finally(() => setLoading(false));
  };

  const manufacturers = useMemo(() => {
    // Hersteller werden aus den Farboptionen abgeleitet. Der Hersteller-Code
    // bleibt der technische Schlüssel; der sichtbare Name dient nur der Anzeige.
    const found = new Map();
    options.forEach((option) => {
      if (!found.has(option.manufacturer_code)) found.set(option.manufacturer_code, option.manufacturer);
    });
    return [...found.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [options]);
  // Exakte Codevergleiche verhindern Sonderfälle wie Mercedes-Benz: Bindestrich
  // und hohe Farbanzahl verändern die Zuordnung nicht.
  const manufacturerColours = useMemo(
    () => options.filter((option) => option.manufacturer_code === manufacturer),
    [options, manufacturer]
  );
  const ralGroups = useMemo(() => [...new Set(options.map((option) => String(option.group || '')))].filter(Boolean).sort(), [options]);
  const ralColours = useMemo(() => options.filter((option) => String(option.group || '') === ralGroup), [options, ralGroup]);

  return (
    <section className="pf-colour" aria-label={t('selectColour')}>
      <span className="pf-field-label">{t('colourType')}</span>
      <div className="pf-colour__types">
        {visibleTypes.map((item) => (
          <button type="button" className={type === item ? 'is-active' : ''} onClick={() => chooseType(item)} key={item}>{t(item)}</button>
        ))}
      </div>

      {loading && <p className="pf-note">{t('loadingColours')}</p>}
      {error && <p className="pf-error">{error}</p>}

      {type === 'standard' && !loading && (
        <div className="pf-colour__panel">
          <label className="pf-field">
            <span>{t('selectColour')}</span>
            <select value={value?.key || ''} onChange={(event) => onChange(options.find((option) => option.key === event.target.value) || null)}>
              <option value="">{t('choose')}</option>
              {options.map((option) => <option value={option.key} key={option.key}>{option.label}</option>)}
            </select>
          </label>
          <div className="pf-swatches">
            {options.map((option) => (
              <button type="button" className={value?.key === option.key ? 'is-active' : ''} title={option.label} aria-label={option.label} onClick={() => onChange(option)} key={option.key}>
                <span style={{backgroundColor: swatch(option.hex)}} />
              </button>
            ))}
          </div>
        </div>
      )}

      {(type === 'auto' || type === 'furniture') && !loading && (
        <div className="pf-colour__panel">
          <label className="pf-field">
            <span>{t('manufacturer')}</span>
            <select value={manufacturer} onChange={(event) => { setManufacturer(event.target.value); onChange(null); }}>
              <option value="">{t('choose')}</option>
              {manufacturers.map(([code, name]) => <option value={code} key={code}>{name}</option>)}
            </select>
          </label>
          {manufacturer && (
            <OptionDropdown label={t('selectColour')} options={manufacturerColours} value={value} onChange={onChange} existingKeys={existingKeys} showSwatch={type === 'auto'} placeholder={t('choose')} />
          )}
        </div>
      )}

      {type === 'ral' && !loading && (
        <div className="pf-colour__panel">
          <label className="pf-field">
            <span>{t('ralGroup')}</span>
            <select value={ralGroup} onChange={(event) => { setRalGroup(event.target.value); onChange(null); }}>
              <option value="">{t('choose')}</option>
              {ralGroups.map((group) => <option value={group} key={group}>RAL {group}</option>)}
            </select>
          </label>
          {ralGroup && <OptionDropdown label={t('selectColour')} options={ralColours} value={value} onChange={onChange} existingKeys={existingKeys} showSwatch placeholder={t('choose')} />}
        </div>
      )}

      {value && (
        <div className="pf-colour__selection">
          <span>{t('selectedColour')}:</span> <strong>{value.label}</strong>
          {value.is_special && <small>{existingKeys.includes(value.key) ? t('noAdditionalFee') : t('feeIncluded')}</small>}
        </div>
      )}
    </section>
  );
}

export function colourPayload(colour, express = false) {
  // Kotori_UrlCartAdd reicht diese Felder unverändert an die zentrale
  // Quote-Validierung weiter. Der Schlüssel allein wäre auflösbar; die übrigen
  // Felder halten die Anfrage transparent und kompatibel zu anderen Widgets.
  if (!colour) return {};
  const payload = {
    kotori_colour_key: colour.key,
    kotori_colour_type: colour.type,
    kotori_colour_code: colour.code || colour.manufacturer_code || '',
    kotori_colour_value: colour.sku_suffix || colour.colour_name || colour.code || '',
    kotori_colour_label: colour.label
  };
  if (express && colour.is_special) payload.kotori_colour_express = '1';
  return payload;
}
